import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ClaimDrawer } from '../components/claims/ClaimDrawer';
import SafeIcon from '../common/SafeIcon';
import { 
  FiSearch, FiCheckCircle, FiTruck, FiBox, 
  FiDollarSign, FiLoader, FiXCircle, FiMoreVertical, 
  FiMessageSquare, FiAlertCircle, FiClock, FiLayers 
} from 'react-icons/fi';
import supabase from '../supabase/supabase';
import { useAuth } from '../context/AuthContext';

// Simple Countdown Timer that triggers an onExpire event when 24h is up
const CountdownTimer = ({ allocatedAt, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const hasExpired = useRef(false);

  useEffect(() => {
    if (!allocatedAt) return;
    const target = new Date(allocatedAt).getTime() + 24 * 60 * 60 * 1000;

    const update = () => {
      const now = new Date().getTime();
      const diff = target - now;
      if (diff <= 0) {
        setTimeLeft('Timed Out');
        if (!hasExpired.current) {
          hasExpired.current = true;
          onExpire();
        }
      } else {
        const h = Math.floor((diff / (1000 * 60 * 60)));
        const m = Math.floor((diff / 1000 / 60) % 60);
        const s = Math.floor((diff / 1000) % 60);
        setTimeLeft(`${h}h ${m}m ${s}s`);
      }
    };
    
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [allocatedAt, onExpire]);

  if (timeLeft === 'Timed Out') {
    return (
      <span className="text-red-600 font-bold text-xs flex items-center gap-1">
        <SafeIcon icon={FiClock} /> Timed Out
      </span>
    );
  }
  
  return (
    <span className="text-orange-600 font-bold text-xs flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-md border border-orange-100 w-fit">
      <SafeIcon icon={FiClock} /> {timeLeft}
    </span>
  );
};

export const SalvagerClaims = () => {
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState('new'); // new, accepted, issue, paid
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Drawer & Context Menu State
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);

  // Modal States
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueReason, setIssueReason] = useState('');
  const [claimToIssue, setClaimToIssue] = useState(null);

  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [claimToOffer, setClaimToOffer] = useState(null);

  const [payModalOpen, setPayModalOpen] = useState(false);
  const [claimToPay, setClaimToPay] = useState(null);

  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [claimToDecline, setClaimToDecline] = useState(null);

  useEffect(() => {
    if (user?.id) fetchClaims();

    const handleClickOutside = () => setContextMenu(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [user]);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('claims_1783447171616')
        .select('*')
        .eq('assigned_salvager_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Ensure any claims that timed out are reverted
      const now = new Date().getTime();
      const validData = [];
      const timedOutIds = [];

      data.forEach(c => {
        if (c.status === 'Assigned' && c.allocated_at && (now - new Date(c.allocated_at).getTime() > 24 * 60 * 60 * 1000)) {
          timedOutIds.push(c.id);
        } else {
          validData.push(c);
        }
      });

      if (timedOutIds.length > 0) {
        await supabase
          .from('claims_1783447171616')
          .update({ status: 'Timed Out', assigned_salvager_id: null })
          .in('id', timedOutIds);
      }

      setClaims(validData);
    } catch (err) {
      console.error('Error fetching my claims:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (claimId, updates) => {
    setActionLoading(claimId);
    try {
      const { error } = await supabase
        .from('claims_1783447171616')
        .update(updates)
        .eq('id', claimId);

      if (error) throw error;
      await fetchClaims();
    } catch (err) {
      console.error('Error updating claim:', err);
      // Fallback revert optimistic update by reloading
      await fetchClaims();
    } finally {
      setActionLoading(null);
      setContextMenu(null);
    }
  };

  const handleAcceptAll = async () => {
    const newCount = claims.filter(c => c.status === 'Assigned').length;
    if (!window.confirm(`Are you sure you want to accept all ${newCount} new claims?`)) return;
    
    setActionLoading('accept-all');
    try {
      const newClaimIds = claims.filter(c => c.status === 'Assigned').map(c => c.id);
      const { error } = await supabase
        .from('claims_1783447171616')
        .update({ status: 'Accepted' })
        .in('id', newClaimIds);
        
      if (error) throw error;
      await fetchClaims();
    } catch (err) {
      console.error('Error accepting all claims:', err);
      alert('Failed to accept all claims.');
    } finally {
      setActionLoading(null);
    }
  };

  // Decline Logic
  const handleDeclineClick = (e, claim) => {
    if(e) e.stopPropagation();
    setClaimToDecline(claim);
    setDeclineModalOpen(true);
  };

  const executeDecline = async () => {
    if (!claimToDecline) return;
    const id = claimToDecline.id;
    setDeclineModalOpen(false);
    setClaimToDecline(null);
    setClaims(prev => prev.map(c => c.id === id ? { ...c, status: 'Declined' } : c));
    await handleUpdateStatus(id, { status: 'Declined' });
  };

  // Payment Simulation Logic
  const handleSimulatePayClick = (e, claim) => {
    if(e) e.stopPropagation();
    setClaimToPay(claim);
    setPayModalOpen(true);
  };

  const executePayment = async () => {
    if (!claimToPay) return;
    const id = claimToPay.id;
    setPayModalOpen(false);
    setClaimToPay(null);
    setClaims(prev => prev.map(c => c.id === id ? { ...c, payment_status: 'Paid', status: 'Completed' } : c));
    setActiveTab('paid');
    await handleUpdateStatus(id, { payment_status: 'Paid', status: 'Completed' });
  };

  const handleSubmitIssue = async () => {
    if (!issueReason.trim() || !claimToIssue) return;
    setActionLoading(claimToIssue.id);
    try {
      const { error: updateError } = await supabase
        .from('claims_1783447171616')
        .update({ status: 'Issue', ber_reason: `ISSUE: ${issueReason}` })
        .eq('id', claimToIssue.id);

      if (updateError) throw updateError;

      await supabase
        .from('claim_notes_1783454000000')
        .insert([{
          claim_id: claimToIssue.id,
          note_text: `Reported Issue: ${issueReason}`,
          author: profile?.company_name || 'Salvager Partner'
        }]);

      await fetchClaims();
      setIssueModalOpen(false);
      setIssueReason('');
      setClaimToIssue(null);
    } catch (err) {
      console.error('Error submitting issue:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmitOffer = async () => {
    if (!offerAmount || isNaN(offerAmount) || !claimToOffer) return;
    setActionLoading(claimToOffer.id);
    try {
      const { error } = await supabase
        .from('claims_1783447171616')
        .update({ offer_price: parseFloat(offerAmount) })
        .eq('id', claimToOffer.id);

      if (error) throw error;
      await fetchClaims();
      setOfferModalOpen(false);
      setOfferAmount('');
      setClaimToOffer(null);
    } catch (err) {
      console.error('Error setting offer:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const requestDemoClaim = async () => {
    setActionLoading('demo');
    try {
      const { error } = await supabase
        .from('claims_1783447171616')
        .insert([{
          claim_number: `DEMO-${Math.floor(Math.random() * 10000)}`,
          make: 'Apple',
          model: 'iPhone 13',
          salvage_item: 'Mobile Device',
          serial_number: 'N/A',
          ber_reason: 'Screen smashed completely',
          status: 'Assigned',
          payment_status: 'Unpaid',
          pickup_status: 'Pending',
          assigned_salvager_id: user.id
        }]);
      if (error) throw error;
      await fetchClaims();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleContextMenu = (e, claim) => {
    e.preventDefault();
    setContextMenu({ x: e.pageX, y: e.pageY, claim });
  };

  const handleRowClick = (claim) => {
    setSelectedClaim(claim);
    setIsDrawerOpen(true);
  };

  const displayedClaims = claims.filter(claim => {
    if (activeTab === 'new') return claim.status === 'Assigned' && claim.payment_status !== 'Paid';
    if (activeTab === 'accepted') return claim.status === 'Accepted' && claim.payment_status !== 'Paid';
    if (activeTab === 'issue') return claim.status === 'Issue' && claim.payment_status !== 'Paid';
    if (activeTab === 'paid') return claim.payment_status === 'Paid';
    return false;
  });

  const TabButton = ({ id, label, icon, count }) => (
    <button 
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all border-b-2 ${
        activeTab === id 
          ? id === 'issue' 
            ? 'border-orange-500 text-orange-600 bg-orange-50/50' 
            : 'border-brand text-brand bg-brand/5'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
      }`}
    >
      <SafeIcon icon={icon} />
      {label}
      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
        activeTab === id 
          ? id === 'issue' ? 'bg-orange-500 text-white' : 'bg-brand text-white'
          : 'bg-gray-200 text-gray-600'
      }`}>
        {count}
      </span>
    </button>
  );

  const newCount = claims.filter(c => c.status === 'Assigned' && c.payment_status !== 'Paid').length;
  const acceptedCount = claims.filter(c => c.status === 'Accepted' && c.payment_status !== 'Paid').length;
  const issueCount = claims.filter(c => c.status === 'Issue' && c.payment_status !== 'Paid').length;
  const paidCount = claims.filter(c => c.payment_status === 'Paid').length;

  return (
    <div className="max-w-7xl mx-auto space-y-6 relative">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">My Claims</h1>
          <p className="text-gray-500">Manage salvage claims explicitly assigned to you by the insurer.</p>
        </div>
        <button 
          onClick={requestDemoClaim}
          disabled={actionLoading === 'demo'}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium text-sm transition-colors shadow-sm"
        >
          {actionLoading === 'demo' ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiBox} />}
          Request Demo Claim
        </button>
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="flex border-b border-gray-100 bg-white overflow-x-auto">
          <TabButton id="new" label="New Claims" icon={FiBox} count={newCount} />
          <TabButton id="accepted" label="Accepted Claims" icon={FiTruck} count={acceptedCount} />
          <TabButton id="issue" label="Issue Claims" icon={FiAlertCircle} count={issueCount} />
          <TabButton id="paid" label="Paid Claims" icon={FiCheckCircle} count={paidCount} />
        </div>

        {activeTab === 'new' && newCount > 1 && (
           <div className="p-4 bg-blue-50 border-b border-blue-100 flex justify-between items-center">
             <div className="flex items-center gap-2 text-blue-700">
               <SafeIcon icon={FiLayers} />
               <span className="text-sm font-medium">You have multiple new claims waiting for your confirmation.</span>
             </div>
             <button
               onClick={handleAcceptAll}
               disabled={actionLoading === 'accept-all'}
               className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2"
             >
               {actionLoading === 'accept-all' ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCheckCircle} />}
               Accept All {newCount} Claims
             </button>
           </div>
        )}

        <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search your assigned claims..." 
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 text-sm"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500 px-2">
            <SafeIcon icon={FiMoreVertical} />
            <span>Right-click any claim for more options</span>
          </div>
        </div>

        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100 text-xs uppercase text-gray-400 tracking-wider">
                <th className="px-6 py-4 font-semibold">Claim Number</th>
                <th className="px-6 py-4 font-semibold">Item Details</th>
                <th className="px-6 py-4 font-semibold">BER Reason / Issue</th>
                <th className="px-6 py-4 font-semibold">Offer Price</th>
                {activeTab === 'new' ? (
                  <th className="px-6 py-4 font-semibold">Time Remaining</th>
                ) : (
                  <th className="px-6 py-4 font-semibold">Pickup Status</th>
                )}
                <th className="px-6 py-4 font-semibold">Payment</th>
                <th className="px-6 py-4 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                    <SafeIcon icon={FiLoader} className="animate-spin inline-block mr-2" /> Loading your claims...
                  </td>
                </tr>
              ) : displayedClaims.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <SafeIcon icon={activeTab === 'new' ? FiBox : activeTab === 'accepted' ? FiTruck : activeTab === 'issue' ? FiAlertCircle : FiCheckCircle} size={48} className="opacity-20" />
                      <p className="italic text-sm">No {activeTab} claims found.</p>
                      {activeTab === 'new' && (
                        <p className="text-xs max-w-sm mt-2 text-center">When the insurer assigns a new salvage claim to your profile, it will appear here for you to accept.</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                displayedClaims.map((claim) => (
                  <tr 
                    key={claim.id} 
                    className={`hover:bg-gray-50/50 transition-colors cursor-pointer ${contextMenu?.claim?.id === claim.id ? 'bg-brand/5' : ''} ${claim.status === 'Issue' ? 'bg-orange-50/30' : ''}`}
                    onClick={() => handleRowClick(claim)}
                    onContextMenu={(e) => handleContextMenu(e, claim)}
                  >
                    <td className="px-6 py-4">
                      <span className="font-bold text-gray-900">{claim.claim_number}</span>
                      <div className="text-xs text-gray-500 mt-1">{new Date(claim.created_at).toLocaleDateString()}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-800">{claim.salvage_item || 'N/A'}</span>
                      <div className="text-gray-500 text-xs mt-0.5">{claim.make} {claim.model}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`${claim.status === 'Issue' ? 'text-orange-600 font-bold' : 'text-red-600 font-medium'} text-xs`}>
                        {claim.ber_reason}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-gray-900 bg-gray-100 px-2 py-1 rounded-lg">
                         R {claim.offer_price ? parseFloat(claim.offer_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                      </span>
                    </td>
                    {activeTab === 'new' ? (
                      <td className="px-6 py-4">
                        {claim.allocated_at ? (
                          <CountdownTimer allocatedAt={claim.allocated_at} onExpire={fetchClaims} />
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                    ) : (
                      <td className="px-6 py-4">
                        <Badge status={claim.pickup_status === 'Picked Up' ? 'Completed' : 'Pending'}>
                          {claim.pickup_status || 'Pending'}
                        </Badge>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${claim.payment_status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                        {claim.payment_status || 'Unpaid'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      {activeTab === 'new' && (
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleUpdateStatus(claim.id, { status: 'Accepted' }); }}
                            disabled={actionLoading === claim.id}
                            className="px-4 py-2 bg-brand text-white rounded-xl text-xs font-bold hover:bg-brand-light transition-all shadow-sm"
                          >
                            {actionLoading === claim.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Accept'}
                          </button>
                          <button 
                            onClick={(e) => handleDeclineClick(e, claim)}
                            disabled={actionLoading === claim.id}
                            className="px-3 py-2 border border-red-200 text-red-600 rounded-xl text-xs font-bold hover:bg-red-50 transition-all"
                            title="Decline Claim"
                          >
                            <SafeIcon icon={FiXCircle} size={16} />
                          </button>
                        </div>
                      )}
                      
                      {activeTab === 'accepted' && (
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={(e) => { e.stopPropagation(); setClaimToOffer(claim); setOfferAmount(claim.offer_price || ''); setOfferModalOpen(true); }}
                            disabled={actionLoading === claim.id}
                            className="px-3 py-1.5 border border-brand text-brand rounded-lg text-xs font-bold hover:bg-brand/5 transition-all"
                          >
                            Set Price
                          </button>
                          {claim.pickup_status !== 'Picked Up' && (
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleUpdateStatus(claim.id, { pickup_status: 'Picked Up' }); }}
                              disabled={actionLoading === claim.id}
                              className="px-3 py-1.5 border border-gray-200 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all"
                            >
                              Mark Picked Up
                            </button>
                          )}
                          <button 
                            onClick={(e) => handleSimulatePayClick(e, claim)}
                            disabled={actionLoading === claim.id}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 transition-all shadow-sm"
                            title="Simulate Admin Payment"
                          >
                            <SafeIcon icon={FiDollarSign} /> Pay
                          </button>
                        </div>
                      )}

                      {(activeTab === 'paid' || activeTab === 'issue') && (
                        <button 
                          onClick={() => handleRowClick(claim)}
                          className="px-3 py-1.5 text-gray-500 hover:text-brand text-xs font-bold transition-all"
                        >
                          View Detail
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Context Menu */}
      {contextMenu && (
        <div 
          className="fixed bg-white shadow-2xl rounded-xl border border-gray-100 py-2 w-56 z-[100] animate-in fade-in zoom-in duration-100"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-2 border-b border-gray-50 mb-1">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{contextMenu.claim.claim_number}</p>
          </div>

          <button 
            onClick={() => { handleRowClick(contextMenu.claim); setContextMenu(null); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 font-medium"
          >
            <SafeIcon icon={FiMessageSquare} className="text-gray-400" /> View Detail & Notes
          </button>

          {activeTab === 'new' && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); handleUpdateStatus(contextMenu.claim.id, { status: 'Accepted' }); setContextMenu(null); }}
                className="w-full text-left px-4 py-2 text-sm text-brand hover:bg-brand/5 flex items-center gap-3 font-medium"
              >
                <SafeIcon icon={FiCheckCircle} className="text-brand/70" /> Accept Claim
              </button>
              <div className="h-px bg-gray-100 my-1 mx-2" />
              <button 
                onClick={(e) => { handleDeclineClick(e, contextMenu.claim); setContextMenu(null); }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 font-medium"
              >
                <SafeIcon icon={FiXCircle} className="text-red-400" /> Decline Claim
              </button>
            </>
          )}

          {activeTab === 'accepted' && (
            <>
              <div className="h-px bg-gray-100 my-1 mx-2" />
              <button 
                onClick={() => { setClaimToOffer(contextMenu.claim); setOfferAmount(contextMenu.claim.offer_price || ''); setOfferModalOpen(true); setContextMenu(null); }}
                className="w-full text-left px-4 py-2 text-sm text-brand hover:bg-brand/5 flex items-center gap-3 font-medium"
              >
                <SafeIcon icon={FiDollarSign} className="text-brand/70" /> Set Offer Price
              </button>

              {contextMenu.claim.pickup_status !== 'Picked Up' && (
                <button 
                  onClick={(e) => { e.stopPropagation(); handleUpdateStatus(contextMenu.claim.id, { pickup_status: 'Picked Up' }); setContextMenu(null); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 font-medium"
                >
                  <SafeIcon icon={FiTruck} className="text-gray-400" /> Mark as Picked Up
                </button>
              )}

              <button 
                onClick={(e) => { handleSimulatePayClick(e, contextMenu.claim); setContextMenu(null); }}
                className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center gap-3 font-medium"
              >
                <SafeIcon icon={FiDollarSign} className="text-green-500" /> Simulate Payment
              </button>
            </>
          )}

          {/* Mark as Issue Feature */}
          {activeTab !== 'paid' && activeTab !== 'issue' && (
            <>
              <div className="h-px bg-gray-100 my-1 mx-2" />
              <button 
                onClick={() => { setClaimToIssue(contextMenu.claim); setIssueModalOpen(true); setContextMenu(null); }}
                className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-3 font-medium"
              >
                <SafeIcon icon={FiAlertCircle} className="text-orange-400" /> Mark as Issue
              </button>
            </>
          )}
        </div>
      )}

      {/* Claim Details Drawer */}
      <ClaimDrawer 
        claim={selectedClaim} 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        onUpdate={fetchClaims}
      />

      {/* Set Offer Price Modal */}
      {offerModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-in fade-in duration-200" onClick={() => setOfferModalOpen(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl p-6 shadow-2xl z-50 w-full max-w-sm animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-brand/10 text-brand flex items-center justify-center">
                <SafeIcon icon={FiDollarSign} size={20} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Set Offer Price</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6 pl-13">
              Enter the amount you are willing to pay for <span className="font-bold text-gray-700">{claimToOffer?.claim_number}</span>.
            </p>
            <div className="relative mb-6">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-gray-500 font-bold">R</span>
              </div>
              <input 
                type="number" 
                value={offerAmount} 
                onChange={(e) => setOfferAmount(e.target.value)}
                placeholder="0.00"
                className="w-full pl-10 p-4 border border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none text-lg font-bold transition-all"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setOfferModalOpen(false)}
                className="px-5 py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmitOffer}
                disabled={!offerAmount || actionLoading === claimToOffer?.id}
                className="px-5 py-2.5 bg-brand text-white text-sm font-bold rounded-xl hover:bg-brand-light disabled:opacity-50 flex items-center gap-2 transition-colors shadow-md"
              >
                {actionLoading === claimToOffer?.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Save Price'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Payment Simulation Custom Modal */}
      {payModalOpen && claimToPay && (
        <>
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-in fade-in duration-200" onClick={() => setPayModalOpen(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl p-6 shadow-2xl z-50 w-full max-w-sm animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                <SafeIcon icon={FiDollarSign} size={20} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Simulate Payment</h3>
            </div>
            
            {(claimToPay.offer_price || claimToPay.estimated_value || 0) <= 0 ? (
              <>
                <p className="text-sm text-red-500 mb-6 pl-13">
                  Please set an Offer Price greater than 0 before simulating payment.
                </p>
                <div className="flex justify-end gap-3">
                  <button 
                    onClick={() => setPayModalOpen(false)}
                    className="px-5 py-2.5 bg-gray-100 text-gray-700 text-sm font-bold rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-6 pl-13">
                  Are you sure you want to simulate a payment of <span className="font-bold text-gray-900">R {parseFloat(claimToPay.offer_price || claimToPay.estimated_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> for <span className="font-bold text-gray-700">{claimToPay.claim_number}</span>?
                </p>
                <div className="flex justify-end gap-3">
                  <button 
                    onClick={() => setPayModalOpen(false)}
                    className="px-5 py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={executePayment}
                    disabled={actionLoading === claimToPay.id}
                    className="px-5 py-2.5 bg-green-600 text-white text-sm font-bold rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 transition-colors shadow-md"
                  >
                    {actionLoading === claimToPay.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Confirm Payment'}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Decline Claim Custom Modal */}
      {declineModalOpen && claimToDecline && (
        <>
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-in fade-in duration-200" onClick={() => setDeclineModalOpen(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl p-6 shadow-2xl z-50 w-full max-w-sm animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                <SafeIcon icon={FiXCircle} size={20} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Decline Claim</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6 pl-13">
              Are you sure you want to decline <span className="font-bold text-gray-700">{claimToDecline.claim_number}</span>? It will be sent back to the insurer.
            </p>
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setDeclineModalOpen(false)}
                className="px-5 py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={executeDecline}
                disabled={actionLoading === claimToDecline.id}
                className="px-5 py-2.5 bg-red-600 text-white text-sm font-bold rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 transition-colors shadow-md"
              >
                {actionLoading === claimToDecline.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Decline'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Report Issue Modal */}
      {issueModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-in fade-in duration-200" onClick={() => setIssueModalOpen(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl p-6 shadow-2xl z-50 w-full max-w-md animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
                <SafeIcon icon={FiAlertCircle} size={20} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Report Issue</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6 pl-13">
              Please provide a reason why <span className="font-bold text-gray-700">{claimToIssue?.claim_number}</span> is an issue. It will be moved to the Issue tab and sent back to the insurer for review.
            </p>
            <textarea 
              value={issueReason}
              onChange={(e) => setIssueReason(e.target.value)}
              placeholder="E.g., Client refused pickup, Item not at location, Details mismatch..."
              className="w-full p-4 border border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 outline-none text-sm min-h-[120px] mb-6 resize-none transition-all"
            />
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setIssueModalOpen(false)}
                className="px-5 py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmitIssue}
                disabled={!issueReason.trim() || actionLoading === claimToIssue?.id}
                className="px-5 py-2.5 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2 transition-colors shadow-md"
              >
                {actionLoading === claimToIssue?.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Submit Issue'}
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
};